import type { TooltipFormatterContextObject, TooltipOptions } from 'highcharts/highcharts';
import { useMemo, type ReactElement } from 'react';
import { useTheme, type DefaultTheme } from 'styled-components';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useIntlContext } from '../../../../hooks/useIntlContext';
import { renderToHTML } from '../../../../utils';
import { BubbleChartTooltip } from '../BubbleChartTooltip';
import type { ChartTooltipValueType, UseChartTooltipProps } from '../types';

export function useBubbleChartTooltip({
  currency,
  xValueType,
  yValueType,
  zValueType,
  xLabel,
  yLabel,
  zLabel,
  omittedSeries,
}: {
  xValueType: ChartTooltipValueType;
  yValueType: ChartTooltipValueType;
  zValueType: ChartTooltipValueType;
  xLabel: string;
  yLabel: string;
  zLabel: string;
} & Omit<UseChartTooltipProps, 'shared' | 'valueType'>): TooltipOptions {
  const homeCurrencyInfo = useCurrency(currency);
  const theme = useTheme();
  const { locale } = useIntlContext();

  return useMemo(() => {
    return {
      useHTML: true,
      padding: 0,
      backgroundColor: 'transparent',
      formatter: function <TReturn = string>(
        this: TooltipFormatterContextObject & {
          renderFunction?: (component: ReactElement, theme: DefaultTheme, locale?: string) => TReturn;
          shared?: boolean;
        }
      ) {
        const renderFunction = this.renderFunction ?? renderToHTML;
        if (this && !omittedSeries?.includes(this.series.name)) {
          return renderFunction(
            <BubbleChartTooltip
              data={this}
              title={this.series.name}
              homeCurrency={homeCurrencyInfo}
              xValueType={xValueType}
              yValueType={yValueType}
              zValueType={zValueType}
              xLabel={xLabel}
              yLabel={yLabel}
              zLabel={zLabel}
            />,
            theme,
            locale
          );
        } else {
          return false;
        }
      },
    };
  }, [omittedSeries, homeCurrencyInfo, xValueType, yValueType, zValueType, xLabel, yLabel, zLabel, theme, locale]);
}
